import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './Navbar.module.css';
import smoothScroll from '../../utils/smoothScroll';

const Navbar = () => {
    const { t, i18n } = useTranslation();
    const [activeLink, setActiveLink] = useState('hero');
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (location.pathname === '/') {
            const sections = document.querySelectorAll('section[id]');
            const navLinks = document.querySelectorAll(`.${styles.navLinks} a[href^="#"]`);

            const handleScroll = () => {
                if (window.scrollY > 50) {
                    setIsScrolled(true);
                } else {
                    setIsScrolled(false);
                }

                let currentActive = 'hero';
                let found = false;
                sections.forEach(section => {
                    const sectionTop = section.offsetTop;
                    if (window.scrollY >= sectionTop - 150) {
                        currentActive = section.id;
                        found = true;
                    }
                });
                if (!found && window.scrollY < sections[0]?.offsetTop -150) {
                    currentActive = 'hero';
                }
                setActiveLink(currentActive);
            };

            if (navLinks.length > 0 && sections.length > 0) {
                handleScroll();
                window.addEventListener('scroll', handleScroll);
                return () => window.removeEventListener('scroll', handleScroll);
            }
        } else {
            setActiveLink('');
        }
    }, [location.pathname]);

    const navItems = [
        { id: 'installation', labelKey: 'navbar.installation' },
        { id: 'software-interactive', labelKey: 'navbar.software' },
        { id: 'benefits', labelKey: 'navbar.advantages' },
        { id: 'mobile-app-feature', labelKey: 'navbar.mobileApp' },
    ];

    const handleInternalNav = (e, targetId) => {
        if (location.pathname === '/') {
            smoothScroll(e, targetId);
        } else {
            // For Link components, React Router handles navigation.
            // If we want to scroll to an anchor after navigating to '/',
            // that would require an additional effect in MainPageLayout or App.js
            // listening to location.hash.
        }
    };

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
    };

    return (
        <nav className={`${styles.navbar} ${isScrolled ? styles.navbarScrolled : ''}`}>
            <Link to="/" className={styles.navbarLogo} onClick={(e) => handleInternalNav(e, 'hero')}>
                <picture>
                  <source media="(max-width: 768px)" srcSet="/images/squared_black.png" />
                  <img src="/images/green-growth-logo-white.png" alt="Green Growth Logo" className={styles.logoImg} />
                </picture>
            </Link>
            <ul className={styles.navLinks}>
                {navItems.map(item => (
                    <li key={item.id}>
                        {location.pathname === '/' ? (
                            <a 
                                href={`#${item.id}`}
                                className={activeLink === item.id ? styles.active : ''}
                                onClick={(e) => smoothScroll(e, item.id)}
                            >
                                {t(item.labelKey)}
                            </a>
                        ) : (
                            <Link 
                                to={`/#${item.id}`}
                                className={styles.navLinkItem}
                            >
                                {t(item.labelKey)}
                            </Link>
                        )}
                    </li>
                ))}
            </ul>
            <div className={styles.navActions}>
                <div className={styles.languageSwitcher}>
                    {isMobile ? (
                        <select
                            value={i18n.language}
                            onChange={e => changeLanguage(e.target.value)}
                            className={styles.languageDropdown}
                        >
                            <option value="en">EN</option>
                            <option value="es">ES</option>
                        </select>
                    ) : (
                        <>
                            <button onClick={() => changeLanguage('en')} className={i18n.language === 'en' ? styles.activeLang : ''}>EN</button>
                            <span>|</span>
                            <button onClick={() => changeLanguage('es')} className={i18n.language === 'es' ? styles.activeLang : ''}>ES</button>
                        </>
                    )}
                </div>
                <a href="https://app.greengrowth.tech/login?now" className={`${styles.btn} ${styles.navButtonLight}`}>{t('navbar.appLogin')}</a>
                {location.pathname === '/' ? (
                     <a href="#cta-form" className={`${styles.btn} ${styles.navButtonPrimary}`} onClick={(e) => smoothScroll(e, 'cta-form')}>{t('navbar.contactUs')}</a>
                ) : (
                    <Link to="/#cta-form" className={`${styles.btn} ${styles.navButtonPrimary}`}>{t('navbar.contactUs')}</Link>
                )}
            </div>
        </nav>
    );
};

export default Navbar;
